export const SITE_ORIGIN = "https://behaviouroperatingsystem.com";
export const DEFAULT_SOCIAL_IMAGE = `${SITE_ORIGIN}/assets/logo-stacked.png`;

export const PAGE_META = {
  "/": {
    title: "Operational Clarity for Reliable Execution | BOS",
    description: "BOS helps organisations identify what keeps recurring execution problems alive, change the conditions leaders control and reduce unnecessary management intervention.",
  },
  "/operational-clarity": {
    title: "What Is Operational Clarity? | Behaviour Operating System",
    description: "Understand the five conditions that help people use judgement, take ownership and execute consistently when priorities compete or work becomes difficult.",
  },
  "/how-bos-works": {
    title: "How BOS Works | From Friction to Clearer Execution",
    description: "See how BOS tests whether stated expectations remain usable in real work and how Operational Clarity supports more reliable execution.",
  },
  "/bos-reveal": {
    title: "BOS Reveal | Recurring Execution Problems",
    description: "A focused consulting engagement that identifies what is keeping one recurring execution problem alive and what leadership should change.",
  },
  "/operational-clarity-review": {
    title: "Free Operational Clarity Review | Behaviour Operating System",
    description: "Check one recurring area of work with a free 18-question review covering direction, standards, ownership, authority, behaviour and day-to-day friction.",
  },
  "/insights": {
    title: "Operational Clarity Insights | Behaviour Operating System",
    description: "Clear thinking about recurring execution problems, Operational Clarity and the conditions that make sound judgement easier or harder in real work.",
  },
  "/executive-guide": {
    title: "BOS Executive Brief | Behaviour Operating System",
    description: "A practical executive brief for leaders examining whether unclear operating conditions are contributing to recurring execution problems — and how Operational Clarity can improve execution.",
  },
  "/about": {
    title: "About BOS and Founder Tristan Todd | Behaviour Operating System",
    description: "Learn why Tristan Todd created Behaviour Operating System and the diagnostic discipline BOS uses to examine recurring operational problems.",
  },
  "/contact": {
    title: "BOS Consultation | Discuss an Execution Problem",
    description: "Describe the recurring execution problem, what should happen instead and where managers keep being drawn in to determine whether BOS is relevant.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Behaviour Operating System",
    description: "How Behaviour Operating System collects, uses, stores and protects personal information.",
  },
  "/terms-of-use": {
    title: "Terms of Use | Behaviour Operating System",
    description: "Terms governing access to and use of the Behaviour Operating System website and its published information.",
  },
  "/subscription-confirmed": {
    title: "Subscription Confirmed | The Operational Brief",
    description: "Confirmation that a subscription to The Operational Brief has been completed.",
    robots: "noindex,follow",
  },
};

export const NOT_FOUND_META = {
  title: "Page Not Found | Behaviour Operating System",
  description: "The requested page could not be found.",
  robots: "noindex,follow",
};

const organization = {
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}/#organization`,
  name: "Behaviour Operating System",
  alternateName: "BOS",
  url: SITE_ORIGIN,
  logo: {
    "@type": "ImageObject",
    url: DEFAULT_SOCIAL_IMAGE,
  },
  founder: { "@id": `${SITE_ORIGIN}/about#tristan-todd` },
  location: {
    "@type": "Place",
    name: "Sydney, Australia",
  },
};

const website = {
  "@type": "WebSite",
  "@id": `${SITE_ORIGIN}/#website`,
  url: `${SITE_ORIGIN}/`,
  name: "Behaviour Operating System",
  publisher: { "@id": `${SITE_ORIGIN}/#organization` },
  inLanguage: "en-AU",
};

const founder = {
  "@type": "Person",
  "@id": `${SITE_ORIGIN}/about#tristan-todd`,
  name: "Tristan Todd",
  url: `${SITE_ORIGIN}/about`,
  jobTitle: "Founder",
  worksFor: { "@id": `${SITE_ORIGIN}/#organization` },
  homeLocation: { "@type": "Place", name: "Sydney, Australia" },
  sameAs: ["https://www.linkedin.com/in/tristanltodd/"],
};

export const siteSchemas = (pathname) => {
  if (pathname === "/") return [organization, website];
  if (pathname === "/about") return [founder];
  return [];
};

export const articleSchemas = (article) => {
  const canonical = `${SITE_ORIGIN}/insights/${article.slug}`;
  const image = article.banner ? `${SITE_ORIGIN}/content/insights/${article.banner}` : DEFAULT_SOCIAL_IMAGE;
  return [
    {
      "@type": "Article",
      "@id": `${canonical}#article`,
      headline: article.title,
      description: article.seoDescription,
      image: [image],
      datePublished: article.date,
      author: {
        "@type": "Person",
        name: article.author || "Tristan Todd",
        url: `${SITE_ORIGIN}/about`,
      },
      publisher: { "@id": `${SITE_ORIGIN}/#organization` },
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      inLanguage: "en-AU",
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${canonical}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Insights", item: `${SITE_ORIGIN}/insights` },
        { "@type": "ListItem", position: 3, name: article.title, item: canonical },
      ],
    },
  ];
};
